import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  ReferenceLine,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useBufferAudioSync } from "../hooks/useBufferAudioSync";

interface RhythmVisualizerProps {
  refEnvelope: number[];
  stuEnvelope: number[];
  bpm: number;
  refUrl?: string;
  stuUrl?: string;

  /**
   * ✅ DURÉE RÉELLE CERTIFIÉE (DSP)
   * Obligatoire pour éviter toute timeline locale basée sur envelope.length.
   * Exemple: result.session.duration
   */
  durationSec?: number;
}

export const RhythmVisualizer: React.FC<RhythmVisualizerProps> = ({
  refEnvelope = [],
  stuEnvelope = [],
  refUrl,
  stuUrl,
  bpm,
  durationSec,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartW, setChartW] = useState(0);
  const [chartH, setChartH] = useState(0);

  const hasRef = !!refUrl && refUrl.trim().length > 0;
  const hasStu = !!stuUrl && stuUrl.trim().length > 0;

  // ✅ WebAudio = source de vérité
  const { isPlaying, currentTime, togglePlay, status } = useBufferAudioSync({
    stuUrl: stuUrl || "",
    refUrl: hasRef ? refUrl : undefined,
    refOffsetMs: 0,
    playbackSpeed: 1.0,
  });

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setChartW(rect.width);
      setChartH(rect.height);
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { chartData, stats, hasData, duration } = useMemo(() => {
    if (!stuEnvelope || stuEnvelope.length === 0 || !bpm) {
      return { chartData: [], stats: null, hasData: false, duration: 0 };
    }

    // ✅ Durée = vérité DSP ONLY
    const d =
      typeof durationSec === "number" && isFinite(durationSec) && durationSec > 0
        ? durationSec
        : 0;

    if (d <= 0) {
      // Fail fast: on refuse toute projection temporelle fausse
      return { chartData: [], stats: null, hasData: false, duration: 0 };
    }

    const steps = 600;
    const data: any[] = [];
    const secPerBeat = 60 / bpm;

    let maxStu = -100;

    for (let i = 0; i < steps; i++) {
      const progress = i / steps;
      const timeSec = progress * d;

      const totalBeats = timeSec / secPerBeat;
      const bar = Math.floor(totalBeats / 4) + 1;
      const beat = Math.floor(totalBeats % 4) + 1;

      const sIdx = Math.max(0, Math.min(stuEnvelope.length - 1, Math.floor(progress * (stuEnvelope.length - 1))));
      const rawS = stuEnvelope[sIdx] || 0;
      let dbS = rawS > 1e-6 ? 20 * Math.log10(rawS) : -60;
      if (!isFinite(dbS)) dbS = -60;
      dbS = Math.max(-60, dbS);

      let dbR = -60;
      if (refEnvelope && refEnvelope.length > 0) {
        const rIdx = Math.max(0, Math.min(refEnvelope.length - 1, Math.floor(progress * (refEnvelope.length - 1))));
        const rawR = refEnvelope[rIdx] || 0;
        dbR = rawR > 1e-6 ? 20 * Math.log10(rawR) : -60;
        if (!isFinite(dbR)) dbR = -60;
        dbR = Math.max(-60, dbR);
      } else {
        dbR = -40;
      }

      if (dbS > maxStu) maxStu = dbS;

      data.push({
        time: timeSec,
        measure: `${bar}.${beat}`,
        ref: dbR,
        stu: dbS,
        diff: Math.abs(dbS - dbR),
      });
    }

    return {
      chartData: data,
      stats: { maxStu: Math.round(maxStu) },
      hasData: true,
      duration: d,
    };
  }, [refEnvelope, stuEnvelope, bpm, durationSec]);

  if (!hasStu) {
    return (
      <div className="bg-[#01080a] p-10 rounded-[64px] border-2 border-dashed border-white/5 shadow-2xl h-[500px] flex flex-col items-center justify-center opacity-60 text-center gap-6">
        <div className="w-20 h-20 rounded-full border-2 border-slate-700 flex items-center justify-center text-4xl">🔇</div>
        <div className="space-y-2">
          <h3 className="text-xl font-black text-white uppercase italic">Audio élève manquant</h3>
          <p className="text-[10px] text-slate-500 uppercase font-bold max-w-xs leading-relaxed">
            stuUrl manquant — impossible de démarrer la lecture.
          </p>
        </div>
      </div>
    );
  }

  // ✅ Message explicite si durationSec absente (plutôt que mentir)
  if (!durationSec || !isFinite(durationSec) || durationSec <= 0) {
    return (
      <div className="bg-[#01080a] p-10 rounded-[64px] border-2 border-dashed border-white/5 shadow-2xl h-[500px] flex flex-col items-center justify-center opacity-60 text-center gap-6">
        <div className="w-20 h-20 rounded-full border-2 border-slate-700 flex items-center justify-center text-4xl">⏱️</div>
        <div className="space-y-2">
          <h3 className="text-xl font-black text-white uppercase italic">Durée session manquante</h3>
          <p className="text-[10px] text-slate-500 uppercase font-bold max-w-xs leading-relaxed">
            durationSec est requis (durée certifiée DSP). Sans ça, le graphe ne peut pas être aligné correctement.
          </p>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="bg-[#01080a] p-10 rounded-[64px] border-2 border-dashed border-white/5 shadow-2xl h-[500px] flex flex-col items-center justify-center opacity-60 text-center gap-6">
        <div className="w-20 h-20 rounded-full border-2 border-slate-700 flex items-center justify-center text-4xl">🔇</div>
        <div className="space-y-2">
          <h3 className="text-xl font-black text-white uppercase italic">Signal Audio Manquant</h3>
          <p className="text-[10px] text-slate-500 uppercase font-bold max-w-xs leading-relaxed">
            Le flux d&apos;énergie n&apos;a pas pu être extrait. L&apos;analyse ne peut pas être rendue.
          </p>
        </div>
      </div>
    );
  }

  // Clamp cursor to duration (avoid drawing line beyond chart)
  const cursorX = Math.max(0, Math.min(duration, currentTime));

  return (
    <div className="bg-[#01080a] p-6 md:p-12 rounded-[64px] border border-white/5 shadow-2xl flex flex-col h-full md:h-[800px] relative overflow-hidden">
      <div className="flex flex-col md:flex-row justify-between items-center mb-10 shrink-0 z-20 gap-6">
        <div className="space-y-1">
          <h3 className="text-4xl font-black text-white italic uppercase tracking-tighter">
            Analyse <span className="text-smart-accent">Dynamique</span>
          </h3>
          <p className="text-[10px] text-slate-500 uppercase font-black tracking-[0.4em]">
            Comparaison de l&apos;intensité RMS (dB) vs {refEnvelope.length > 0 ? "Master" : "Baseline"}
          </p>
        </div>
        <div className="text-right">
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Peak Level (You)</span>
          <div className="text-2xl font-black text-white italic">{stats?.maxStu} dB</div>
          <div className="text-[10px] text-slate-500">{status === "loading" ? "Loading audio..." : ""}</div>
        </div>
      </div>

      <div
        ref={chartRef}
        className="flex-1 w-full bg-[#050b0d] rounded-[48px] border border-white/5 overflow-hidden relative shadow-inner group"
      >
        {chartW > 0 && (
          <ComposedChart
            width={chartW}
            height={chartH}
            data={chartData}
            margin={{ top: 40, bottom: 40, left: 10, right: 10 }}
          >
            <defs>
              <linearGradient id="gradRef" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradStu" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FF6261" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#FF6261" stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical />

            <XAxis
              dataKey="time"
              type="number"
              domain={[0, duration]}
              stroke="#475569"
              fontSize={10}
              tickFormatter={(val) => `${Number(val).toFixed(1)}s`}
            />

            <YAxis
              domain={[-60, 0]}
              stroke="#475569"
              fontSize={10}
              label={{
                value: "dB RMS",
                angle: -90,
                position: "insideLeft",
                fill: "#475569",
                fontSize: 9,
                fontWeight: 900 as any,
              }}
            />

            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const d: any = payload[0].payload;
                  return (
                    <div className="bg-black/90 border border-white/20 p-4 rounded-2xl backdrop-blur-md">
                      <p className="text-[10px] font-black text-smart-gold uppercase mb-1">Bar {d.measure}</p>
                      <p className="text-xs text-white">
                        Vous:{" "}
                        <span className="text-smart-accent font-bold">
                          {isFinite(d.stu) ? d.stu.toFixed(1) : "—"} dB
                        </span>
                      </p>
                      <p className="text-xs text-white">
                        {refEnvelope.length > 0 ? "Master" : "Baseline"}:{" "}
                        <span className="text-cyan-400 font-bold">
                          {isFinite(d.ref) ? d.ref.toFixed(1) : "—"} dB
                        </span>
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />

            <Area
              type="stepAfter"
              dataKey="ref"
              stroke="#22d3ee"
              strokeWidth={1}
              fill="url(#gradRef)"
              isAnimationActive={false}
              strokeDasharray={refEnvelope.length > 0 ? "" : "5 5"}
            />
            <Area
              type="monotone"
              dataKey="stu"
              stroke="#FF6261"
              strokeWidth={2}
              fill="url(#gradStu)"
              isAnimationActive={false}
            />

            <ReferenceLine x={cursorX} stroke="#fff" strokeWidth={2} />
          </ComposedChart>
        )}
      </div>

      <div className="mt-8 flex justify-center">
        <button
          onClick={togglePlay}
          className={`px-12 py-5 rounded-full flex items-center gap-4 transition-all ${
            isPlaying ? "bg-red-600" : "bg-white text-black hover:scale-105 shadow-xl"
          }`}
        >
          <span className="text-xl">{isPlaying ? "⏸" : "▶"}</span>
          <span className="text-xs font-black uppercase tracking-[0.2em]">
            {isPlaying ? "PAUSE SYNC" : "ÉCOUTE COMPARATIVE"}
          </span>
        </button>
      </div>
    </div>
  );
};
